import { useLogError } from '@/src/common/hooks/useLogError'
import { useStatsigLoadingStatus } from '@/src/services/Statsig'
import { Platform, Linking, Text } from 'react-native'
import { useMemo, type PropsWithChildren } from 'react'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Button } from '@/src/common/components/Button'
import semver from 'semver'
import { nativeApplicationVersion } from 'expo-application'
import {
  VersionWallMobileSchema,
  useVersionWallParameterStore,
} from '@/src/experiences/updates/statsig/store/useVersionWallStore'
import { mesaColorNames, typography } from '@/src/common/theme'

const mobileStoreUrl = Platform.select({
  ios: 'https://apps.apple.com/us/app/mesa/id6553957119',
  android: 'https://play.google.com/store/apps/details?id=com.mesamember.mesa',
})

function VersionWall({ children }: PropsWithChildren) {
  const logError = useLogError()
  const statsigLoadingStatus = useStatsigLoadingStatus()
  const versionWallStore = useVersionWallParameterStore()

  const isAppOutOfDate = useMemo(() => {
    if (Platform.OS === 'web' || statsigLoadingStatus !== 'Ready' || !nativeApplicationVersion)
      return false

    const minimumSupportedVersion = versionWallStore.get('mobile', nativeApplicationVersion)
    const validate = VersionWallMobileSchema.safeParse(minimumSupportedVersion)
    if (!validate.success) logError(validate.error)

    return semver.lt(
      nativeApplicationVersion,
      validate.success ? validate.data : nativeApplicationVersion,
    )
  }, [statsigLoadingStatus, logError, versionWallStore])

  if (!mobileStoreUrl || !isAppOutOfDate) {
    return children
  }

  return (
    <SafeAreaView
      style={{
        flex: 1,
        alignSelf: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
        paddingHorizontal: 32,
      }}
    >
      <Text style={typography['heading/heading2']}>Update Available</Text>
      <Text
        style={{
          marginVertical: 16,
          marginBottom: 32,
          lineHeight: 24,
          color: mesaColorNames['Greyscale/600 #505D64'],
          textAlign: 'center',
        }}
      >
        Mesa is committed to continuously improving the app experience for homeowners. Tap "Update"
        to get the latest features, security enhancments, and bug fixes.
      </Text>
      <Button
        title="Update"
        segmentAction='User Pressed "Update" Button To Update App'
        onPress={() => void Linking.openURL(mobileStoreUrl)}
        style={{ marginTop: 0, marginBottom: 0 }}
      />
    </SafeAreaView>
  )
}

export const VersionWallScreen = {
  name: 'VersionWall',
  Screen: VersionWall,
} as const
